import {
  TextInput,
  FormLabel,
  FormControl,
  Button,
  Select,
  H3,
  Box,
} from '@ouster/webviz-react-components';

import { FormEvent, useState } from 'react';

import styles from './Organization.module.css';
import { UserRole } from '../../types';
import { ROLES } from '../../utils/constants';

type InviteMemberProps = {
  inviteHandler: (
    data: {
      emails: string[];
      role: UserRole;
    },
    onSuccess: () => void
  ) => void;
  isOusterUser: boolean;
  is_provisioned?: boolean;
};

export default function InviteMember({
  inviteHandler,
  isOusterUser,
  is_provisioned = false,
}: InviteMemberProps) {
  const [isSetupConfirmed, setIsSetupConfirmed] = useState(false);

  let availableRoles: UserRole[] = [];

  if (is_provisioned) {
    availableRoles = ['ADMIN'];
  } else if (!isOusterUser) {
    availableRoles = ROLES.filter((role) => role !== 'SUPPORT');
  } else {
    // Ouster users get all roles including SUPPORT
    availableRoles = [...ROLES];
  }
  const isFormDisabled = is_provisioned && !isSetupConfirmed;

  function resetForm() {
    const form = document.getElementById('invite-form') as HTMLFormElement;
    form.reset();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const emailsInput = formData.get('email') as string;
    const role = formData.get('role') as UserRole;

    if (!emailsInput || !role) return;

    // Split by comma, trim whitespace, and filter out empty strings
    const emails = emailsInput
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (emails.length === 0) return;

    // Send all invitations at once
    inviteHandler({ emails, role }, resetForm);
  }

  return (
    <div
      className={styles['section-container']}
      data-testid="invite-member-root"
    >
      <H3 fontSize={16} textTransform="uppercase" className={styles.heading}>
        Invite Members
      </H3>

      {is_provisioned && (
        <Box
          p={4}
          mb={2}
          borderRadius={4}
          bgColor="yellow.900"
          border="1px solid"
          borderColor="yellow.400"
          data-testid="provisioned-warning-box"
        >
          <label
            htmlFor="setup-confirmation"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              margin: 0,
            }}
          >
            <input
              id="setup-confirmation"
              type="checkbox"
              checked={isSetupConfirmed}
              onChange={(e) => setIsSetupConfirmed(e.target.checked)}
              data-testid="setup-confirmation-checkbox"
            />
            <span>
              I confirm that the organization is fully set up. Once I send this
              invitation, the invited user can claim this organization at any
              time.
            </span>
          </label>
        </Box>
      )}

      <form
        onSubmit={handleSubmit}
        id="invite-form"
        data-testid="invite-member-form"
      >
        <FormControl paddingInline={0}>
          <FormLabel fontSize={13}>
            Enter emails (comma-separated) to invite multiple users to the same
            role.{' '}
            {is_provisioned ? (
              <Box as="span" color="yellow.300">
                Only admins can be invited during provisioning.
              </Box>
            ) : null}
          </FormLabel>
          <Box className={styles['invite-form']}>
            <TextInput
              type="email"
              multiple
              isRequired
              name="email"
              flex={1}
              placeholder="invitee1@email.com, invitee2@email.com"
              data-testid="invite-member-email-input"
              isDisabled={isFormDisabled}
            />
            <Select
              placeholder="Select invitee's role"
              borderRadius={4}
              isRequired
              textTransform="capitalize"
              name="role"
              data-testid="invite-member-role-select"
              disabled={isFormDisabled}
            >
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Select>
            <Button
              variant="outline"
              borderRadius={4}
              minWidth={150}
              type="submit"
              data-testid="invite-member-submit-btn"
              disabled={isFormDisabled}
            >
              Send Invitation
            </Button>
          </Box>
        </FormControl>
      </form>
    </div>
  );
}
